import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import { CaptureConsole, ExtraErrorData, Dedupe } from "@sentry/integrations";
import { createElement } from "react";
import type { ComponentProps } from "react";
import { captureExceptionForPosthog } from "@v2/shared/lib/posthog/posthogCapturing";
import { ENVS } from "@v2/shared/environment.config";
import { handleErrorIfPossible } from "./sentryErrorHandler.lib";
import { devLog, devWarn } from "../logger";
import { isInIframe } from "../runtime";
import {
  isMirroringEventsToPosthogEnabled,
  isTrackingInSentryEnbaled,
} from "@v2/shared/config/features.config";

/**
 * AIDEV-NOTE: Sentry bootstrap for Bulk Actions PUP.
 * - Mirrors exceptions to PostHog (flag-gated) using a single helper.
 * - Scrubs long hex tokens from payload before send.
 * - Detects or sets runtime mode tag (standalone vs embedded) for clearer grouping.
 */

const depth = 10;

// AIDEV-NOTE: Lazy init keeps iframe connector route free from tracing noise
export class SentryManager {
  private static initialized = false;

  static getClient(): typeof Sentry {
    return this.ensureInitialized();
  }

  static setUser(memberId: string | null | undefined) {
    if (!memberId) return;
    try {
      this.ensureInitialized().setUser({ id: memberId });
    } catch {
      // swallow
    }
  }

  /**
   * Set current session id for correlation in Sentry (optional).
   */
  static setPaywallSessionId(id: string) {
    try {
      this.ensureInitialized().setTag("sessionId", id);
    } catch {
      // swallow
    }
  }

  static ensureInitialized(): typeof Sentry {
    if (this.initialized) {
      return Sentry;
    }

    Sentry.init({
      // AIDEV-NOTE: Keep Sentry canonical; drop events in dev unless explicitly enabled
      enabled: true,
      dsn: ENVS.sentryDsn,
      integrations: [
        new BrowserTracing({
          // AIDEV-NOTE: Derived from environment to avoid broad matches
          tracePropagationTargets: ENVS.sentryTraceTargets,
        }),
        new CaptureConsole({ levels: ["error", "debug"] }), //todo[C]: Add "warn"? If so, make sure no warns execute in beforeSend(), because they might re-trigger sentry event.
        new ExtraErrorData({ depth }),
        // AIDEV-NOTE: Dedupe suppresses identical repeats per session
        new Dedupe(),
      ],
      environment: ENVS.environment,
      normalizeDepth: depth + 1,
      tracesSampleRate: ENVS.sentryTracesSampleRate,
      // AIDEV-NOTE: Accept Sentry hint to forward the original Error to PostHog for stable grouping
      beforeSend: (_event, hint): Sentry.Event | null => {
        // warn: do not use console.error() or console.debug() here (see CaptureConsole integration), because it will trigger a sentry event, thus creating an infinite loop!
        const event = handleErrorIfPossible(_event, hint);

        if (!event) {
          return null;
        }

        devLog(
          "Event to be sent to Sentry",
          hint,
          _event.exception?.values?.[0]
        );

        const scrubbedEvent = buildScrubbedEvent(event);
        // Flag-gated mirror; avoid toggling PostHog state at runtime, just gate sends here
        try {
          if (isMirroringEventsToPosthogEnabled()) {
            captureExceptionForPosthog(hint?.originalException, scrubbedEvent);
          }
        } catch (err) {
          devWarn("[Mirror] PostHog mirror failed in beforeSend", err);
        }

        // In non-prod, drop Sentry events unless explicitly enabled
        if (!isTrackingInSentryEnbaled()) {
          return null;
        }
        return scrubbedEvent;
      },
    });

    this.initialized = true;
    return Sentry;
  }
}

export function SentryErrorBoundary(
  props: ComponentProps<typeof Sentry.ErrorBoundary>
) {
  const client = SentryManager.getClient();
  const Boundary = client.ErrorBoundary;
  return createElement(Boundary, props);
}

/**
 * Build a scrubbed copy of the Sentry event:
 * - Mask 64-char hex tokens in the JSON payload
 * - Add stable tags (app, side)
 * - Add current route in extra for convenience
 */
function buildScrubbedEvent(event: Sentry.Event): Sentry.Event {
  const cloned = JSON.parse(
    JSON.stringify(event).replace(
      /[0-9a-f]{64}/gi,
      (match) => "*****************token*****************" + match.slice(-8)
    )
  );
  const mode = detectRuntimeMode();
  return {
    ...cloned,
    tags: {
      ...event?.tags,
      app: "bulk-actions",
      side: "client",
      mode,
      environment: ENVS.environment,
      gitCommit: ENVS.gitCommit,
    },
    extra: {
      ...(event?.extra || {}),
      route:
        typeof window !== "undefined" ? window.location?.pathname : undefined,
    },
  } as Sentry.Event;
}

/**
 * Detect runtime mode for tagging: embedded (iframe) vs standalone.
 * Cross-origin access may throw; assume embedded in that case.
 */
function detectRuntimeMode(): "standalone" | "embedded" {
  return isInIframe() ? "embedded" : "standalone";
}
